import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useGlobalToastNotificationContext } from "../../context/GlobalToastNotificationContext";
import { useApiRequest } from "../../hooks/useApiRequest";
import { deepEqual, OrderCard } from "../../types/orderCard";
import { OrderCardEditResponse } from "./orderCardEditResponse";

interface OrderCardContextType {
  isAutoSaved: boolean;
  onCardInView: (orderCard: OrderCard) => void;
  setEditingOrderCard: (orderCard: OrderCard | null) => void;
  saveOrderCard: (
    orderCardGuid: string,
    cardData: OrderCard["cardData"],
    subgroupName?: string,
    successCallback?: (orderCardEditResponse: OrderCardEditResponse<OrderCard>) => void,
  ) => void;
  jumpToCardGuid?: string | null;
}

const OrderCardContext = createContext<OrderCardContextType>({
  isAutoSaved: false,
  onCardInView: () => {},
  setEditingOrderCard: () => {},
  saveOrderCard: () => {},
  jumpToCardGuid: null,
});

export const useOrderCard = () => {
  const context = useContext(OrderCardContext);
  if (!context) {
    throw new Error("OrderCardProvider is required");
  }
  return context;
};

export const OrderCardProvider: React.FC<{
  onCurrentCardInViewChange: (orderCardGuid: OrderCard) => void;
  onCurrentEditingOrderCardChange: (orderCardGuid: OrderCard | null) => void;
  onOrderCardEdited: (
    orderCardEditResponse: OrderCardEditResponse<OrderCard>
  ) => void;
  children: ReactNode;
  jumpToCardGuid?: string | null;
  orderCard: OrderCard | null;
  onOrderCardRefreshed: (
    orderCardGetResponse: OrderCard
  ) => void,
  saveOrderCardCount: number;
  setSaveOrderCardCount: (value: number) => void;
}> = ({
  onOrderCardEdited,
  onCurrentCardInViewChange,
  onCurrentEditingOrderCardChange,
  jumpToCardGuid,
  orderCard,
  children,
  onOrderCardRefreshed,
  saveOrderCardCount,
  setSaveOrderCardCount,
}) => {
  const { showErrorToast } = useGlobalToastNotificationContext();
  const [isAutoSaved, setIsAutoSaved] = useState(false);

  const {
    data: saveOrderCardData,
    loading: saveOrderCardLoading,
    request: saveOrderCardRequest,
    status: saveOrderCardStatus,
    errorMessage: saveOrderCardErrorMessage,
  } = useApiRequest<OrderCardEditResponse<OrderCard>>(false, "orderCard");

  const {
    request: getOneOrderCardRequest,
  } = useApiRequest<OrderCard>(false, "orderCard");

  useEffect(() => {
    if (
      saveOrderCardData &&
      saveOrderCardStatus === "ok" &&
      saveOrderCardLoading === false &&
      saveOrderCardCount > 0
    ) {
      setSaveOrderCardCount(saveOrderCardCount-1);
      onOrderCardEdited(saveOrderCardData);

      setIsAutoSaved(true);

      const timeout = setTimeout(() => {
        setIsAutoSaved(false);
      }, 500);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [
    onOrderCardEdited,
    saveOrderCardData,
    saveOrderCardLoading,
    saveOrderCardStatus,
    setSaveOrderCardCount,
    saveOrderCardCount,
  ]);

  useEffect(() => {
    if (saveOrderCardErrorMessage) {
      setSaveOrderCardCount(saveOrderCardCount-1);
      showErrorToast({ message: saveOrderCardErrorMessage });
      // Needs to reload the card here if there's an error!
      getOneOrderCardRequest(`/ordercards/${orderCard?.orderCardGuid}`, {
        method: "GET"
      }, {
        onSuccess: (data) => onOrderCardRefreshed(data)
      });
    }
  }, [saveOrderCardErrorMessage, showErrorToast, getOneOrderCardRequest, orderCard?.orderCardGuid, onOrderCardRefreshed, setSaveOrderCardCount, saveOrderCardCount]);

  const saveOrderCard = useCallback(
    (
      orderCardGuid: string,
      cardData: OrderCard["cardData"],
      subgroupName?: string,
      successCallback?: (orderCardEditResponse: OrderCardEditResponse<OrderCard>) => void,
    ) => {
      if (
        (orderCard?.cardData &&
          cardData &&
          !deepEqual(cardData, orderCard.cardData)) ||
        (!!subgroupName && orderCard?.subgroupName !== subgroupName)
      ) {
        setSaveOrderCardCount(saveOrderCardCount+1);
        saveOrderCardRequest(`/ordercards/${orderCardGuid}/edit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          data: { cardData: cardData, subgroupName },
        },{
          onSuccess: successCallback
        });
      }
    },
    [saveOrderCardRequest, orderCard, setSaveOrderCardCount, saveOrderCardCount]
  );

  const onCardInView = useCallback(
    (orderCard: OrderCard) => {
      onCurrentCardInViewChange(orderCard);
    },
    [onCurrentCardInViewChange]
  );

  const setEditingOrderCard = useCallback(
    (orderCard: OrderCard | null) => {
      onCurrentEditingOrderCardChange(orderCard);
    },
    [onCurrentEditingOrderCardChange]
  );

  const contextValue = useMemo(
    () => ({
      isAutoSaved,
      onCardInView,
      saveOrderCard,
      setEditingOrderCard,
      jumpToCardGuid,
      orderCard,
    }),
    [
      isAutoSaved,
      onCardInView,
      saveOrderCard,
      setEditingOrderCard,
      jumpToCardGuid,
      orderCard,
    ]
  );

  return (
    <OrderCardContext.Provider value={contextValue}>
      {children}
    </OrderCardContext.Provider>
  );
};
